import { makeObservable, computed } from "mobx";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import { SpaceAccountCalendarModelData } from "@/store/calendar/space-account-calendar/types";
import { SyncUpdateValue } from "@/store/sync/types";
import { getCalendarSyncStatus } from "@/domains/calendar/getCalendarSyncStatus";
import type { CalendarSyncStatus } from "@/domains/calendar/getCalendarSyncStatus";

export class SpaceAccountCalendarObservable extends BaseSyncModel<SpaceAccountCalendarModelData> {
  public modelKind = SyncModelKind.SpaceAccountCalendar;

  constructor({
    id,
    data,
    store,
  }: { id: string; data: SyncUpdateValue<SpaceAccountCalendarModelData> } & WithAppStore) {
    super({ id, data, store });

    makeObservable(this, {
      modelKind: false,
      associatedConnectedCalendarId: computed,
      associatedSpaceAccountCalendarConnectionId: computed,
      associatedSpaceAccountCalendarConnection: computed,
      primaryLabel: computed,
      color: computed,
      webhookStatus: computed,
      syncStartedAt: computed,
      syncEndedAt: computed,
      lastSyncEndedWithError: computed,
      calendarSyncStatus: computed,
    });
  }

  get associatedConnectedCalendarId() {
    return this.modelData.associated_connected_calendar_id;
  }

  get associatedSpaceAccountCalendarConnectionId() {
    return this.modelData.associated_space_account_calendar_connection_id;
  }

  get associatedSpaceAccountCalendarConnection() {
    return this.store.spaceAccountCalendarConnections.get(
      this.associatedSpaceAccountCalendarConnectionId
    );
  }

  get primaryLabel() {
    return this.modelData.primary_label;
  }

  get color() {
    return this.modelData.background_color || undefined;
  }

  get webhookStatus() {
    return this.modelData.webhook_status ?? null;
  }

  get syncStartedAt() {
    return this.modelData.sync_started_at ?? null;
  }

  get syncEndedAt() {
    return this.modelData.sync_ended_at ?? null;
  }

  get lastSyncEndedWithError() {
    return this.modelData.last_sync_ended_with_error ?? null;
  }

  get calendarSyncStatus(): CalendarSyncStatus | null {
    return getCalendarSyncStatus({
      webhookStatus: this.webhookStatus,
      syncStartedAt: this.syncStartedAt,
      syncEndedAt: this.syncEndedAt,
      lastSyncEndedWithError: this.lastSyncEndedWithError,
      // It needs to be explicitly isConnected=false, not undefined
      isConnected: this.associatedSpaceAccountCalendarConnection?.isConnected ?? true,
    });
  }
}
